"use client";

import { useEffect, useMemo, useState } from "react";
import { RiCalendarCheckLine } from "@remixicon/react";
import {
  addDays,
  addMonths,
  addWeeks,
  endOfWeek,
  format,
  startOfWeek,
  subMonths,
  subWeeks,
} from "date-fns";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "../event-calendar/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "../event-calendar/ui/dropdown-menu";
import { AgendaView } from "../event-calendar/agenda-view";
import { MonthView } from "../event-calendar/month-view";
import { WeekView } from "../event-calendar/week-view";
import EmployeeDayView from "./employee-day-view";
import {
  AgendaDaysToShow,
  EventGap,
  EventHeight,
  WeekCellsHeight,
} from "../event-calendar/constants";

const VIEW_OPTIONS = [
  { value: "month", label: "Month", shortcut: "M" },
  { value: "week", label: "Week", shortcut: "W" },
  { value: "day", label: "Day", shortcut: "D" },
  { value: "agenda", label: "Agenda", shortcut: "A" },
];

export function EmployeeEventCalendar({
  events = [],
  className,
  initialView = "week",
  onToggleSidebar,
  isSidebarOpen,
  currentDate: controlledCurrentDate,
  onCurrentDateChange,
  timezone,
  timeFormat = "24h",
  weekStartsOn = 1,
  businessHours = { startHour: 8, endHour: 20 },
  onViewChange,
  onEventSelect,
}) {
  const isControlled = controlledCurrentDate instanceof Date;
  const [uncontrolledDate, setUncontrolledDate] = useState(
    controlledCurrentDate || new Date()
  );
  const currentDate = isControlled ? controlledCurrentDate : uncontrolledDate;

  const setCurrentDate = (date) => {
    if (!isControlled) {
      setUncontrolledDate(date);
    }
    onCurrentDateChange?.(date);
  };

  const [view, setView] = useState(initialView);
  const handleUpdateView = (nextView) => {
    setView(nextView);
    onViewChange?.(nextView);
  };

  const handlePrevious = () => {
    if (view === "month") {
      setCurrentDate(subMonths(currentDate, 1));
    } else if (view === "week") {
      setCurrentDate(subWeeks(currentDate, 1));
    } else if (view === "day") {
      setCurrentDate(addDays(currentDate, -1));
    } else if (view === "agenda") {
      setCurrentDate(addDays(currentDate, -AgendaDaysToShow));
    }
  };

  const handleNext = () => {
    if (view === "month") {
      setCurrentDate(addMonths(currentDate, 1));
    } else if (view === "week") {
      setCurrentDate(addWeeks(currentDate, 1));
    } else if (view === "day") {
      setCurrentDate(addDays(currentDate, 1));
    } else if (view === "agenda") {
      setCurrentDate(addDays(currentDate, AgendaDaysToShow));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleEventClick = (event, e) => {
    e?.stopPropagation?.();
    onEventSelect?.(event);
  };

  const viewTitle = useMemo(() => {
    if (view === "month") {
      return format(currentDate, "MMMM yyyy");
    }
    if (view === "week") {
      const start = startOfWeek(currentDate, { weekStartsOn: weekStartsOn === 0 ? 0 : 1 });
      const end = endOfWeek(currentDate, { weekStartsOn: weekStartsOn === 0 ? 0 : 1 });
      if (format(start, "MMM") === format(end, "MMM")) {
        return `${format(start, "MMM d")} – ${format(end, "d, yyyy")}`;
      }
      return `${format(start, "MMM d")} – ${format(end, "MMM d, yyyy")}`;
    }
    if (view === "day") {
      return format(currentDate, "EEEE, MMMM d, yyyy");
    }
    if (view === "agenda") {
      const start = currentDate;
      const end = addDays(currentDate, AgendaDaysToShow - 1);
      return `${format(start, "MMM d") } – ${format(end, "MMM d, yyyy")}`;
    }
    return format(currentDate, "MMMM yyyy");
  }, [currentDate, view, weekStartsOn]);

  return (
    <div
      className="flex flex-col rounded-lg"
      style={{
        "--event-height": `${EventHeight}px`,
        "--event-gap": `${EventGap}px`,
        "--week-cells-height": `${WeekCellsHeight}px`,
      }}
    >
      <div
        className={cn(
          "flex items-center justify-between p-3 px-6 sm:px-6 sm:p-6",
          className
        )}
      >
        <div className="flex items-center gap-1 sm:gap-4">
          {onToggleSidebar && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="hidden md:inline-flex"
              onClick={onToggleSidebar}
              aria-label={
                isSidebarOpen
                  ? "Hide calendar sidebar"
                  : "Show calendar sidebar"
              }
            >
              {isSidebarOpen ? (
                <PanelLeftClose size={16} aria-hidden="true" />
              ) : (
                <PanelLeftOpen size={16} aria-hidden="true" />
              )}
            </Button>
          )}
          {onToggleSidebar && (
            <Button
              type="button"
              variant="outline"
              className="md:hidden"
              onClick={onToggleSidebar}
            >
              {isSidebarOpen ? (
                <PanelLeftClose
                  size={16}
                  aria-hidden="true"
                  className="mr-1"
                />
              ) : (
                <PanelLeftOpen
                  size={16}
                  aria-hidden="true"
                  className="mr-1"
                />
              )}
              {isSidebarOpen ? "Close" : "Calendars"}
            </Button>
          )}
          <Button
            variant="outline"
            className="max-[479px]:aspect-square max-[479px]:p-0"
            onClick={handleToday}
          >
            <RiCalendarCheckLine
              className="min-[480px]:hidden"
              size={16}
              aria-hidden="true"
            />

            <span className="">Today</span>
          </Button>
          <div className="flex items-center sm:gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={handlePrevious}
              aria-label="Previous"
            >
              <ChevronLeftIcon size={16} aria-hidden="true" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleNext}
              aria-label="Next"
            >
              <ChevronRightIcon size={16} aria-hidden="true" />
            </Button>
          </div>
          <h2 className="text-sm font-semibold sm:text-lg md:text-xl">
            {viewTitle}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-1.5 max-[479px]:h-8">
                <span>
                  <span className="min-[480px]:hidden" aria-hidden="true">
                    {view.charAt(0).toUpperCase()}
                  </span>
                  <span className="max-[479px]:sr-only">
                    {view.charAt(0).toUpperCase() + view.slice(1)}
                  </span>
                </span>
                <ChevronDownIcon
                  className="-me-1 opacity-60"
                  size={16}
                  aria-hidden="true"
                />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-32">
              {VIEW_OPTIONS.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => handleUpdateView(option.value)}
                >
                  {option.label}
                  <DropdownMenuShortcut>{option.shortcut}</DropdownMenuShortcut>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="flex flex-1 flex-col">
        {view === "month" && (
          <MonthView
            currentDate={currentDate}
            events={events}
            onEventSelect={handleEventClick}
            onEventCreate={() => {}}
            timezone={timezone}
            timeFormat={timeFormat}
          />
        )}
        {view === "week" && (
          <WeekView
            currentDate={currentDate}
            events={events}
            onEventSelect={handleEventClick}
            onEventCreate={() => {}}
            timezone={timezone}
            timeFormat={timeFormat}
            weekStartsOn={weekStartsOn}
            businessHours={businessHours}
          />
        )}
        {view === "day" && (
          <EmployeeDayView
            currentDate={currentDate}
            events={events}
            onEventSelect={handleEventClick}
            timezone={timezone}
            timeFormat={timeFormat}
            businessHours={businessHours}
          />
        )}
        {view === "agenda" && (
          <AgendaView
            currentDate={currentDate}
            events={events}
            onEventSelect={handleEventClick}
            timezone={timezone}
            timeFormat={timeFormat}
          />
        )}
      </div>
    </div>
  );
}

export default EmployeeEventCalendar;
